import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { ensureCsrfCookie, httpClient } from '@/shared/lib/httpClient'
import { apiV1 } from '@/shared/api/apiPrefix'
import { useCurrentUser } from '@/shared/api/currentUser'

/**
 * Property favorites for the signed-in employee. Lives in `shared/` so any feature
 * (reservation cards, home teasers) can toggle a favorite without cross-feature coupling.
 */
// `/me/favorites/ids` returns every favorite id (unpaginated) so hearts render correctly on
// any card, including favorites of properties not on the current page.
export const favoritesKeys = {
  ids: ['favorites', 'ids'] as const,
}

/** The set of favorited property ids, for driving the heart toggle state. */
export function useFavoriteIds() {
  const { data: currentUser } = useCurrentUser()

  return useQuery({
    queryKey: favoritesKeys.ids,
    queryFn: async (): Promise<Set<string>> => {
      const favoriteIdsResponseSchema = z.object({ data: z.array(z.coerce.string().min(1)) })
      const res = await httpClient.get<unknown>(apiV1('/me/favorites/ids'))
      return new Set(favoriteIdsResponseSchema.parse(res.data).data)
    },
    enabled: Boolean(currentUser?.id),
    staleTime: 60_000,
  })
}

export function useToggleFavorite() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({ propertyId, isFavorite }: { propertyId: string; isFavorite: boolean }): Promise<void> => {
      await ensureCsrfCookie()
      const path = apiV1(`/me/favorites/${encodeURIComponent(propertyId)}`)
      if (isFavorite) {
        await httpClient.delete<null>(path)
      } else {
        await httpClient.post<null>(path)
      }
    },
    onMutate: async ({ propertyId, isFavorite }) => {
      await qc.cancelQueries({ queryKey: favoritesKeys.ids })
      const previous = qc.getQueryData<Set<string>>(favoritesKeys.ids)
      const next = new Set(previous ?? [])
      if (isFavorite) {
        next.delete(propertyId)
      } else {
        next.add(propertyId)
      }
      qc.setQueryData(favoritesKeys.ids, next)

      return { previous }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous === undefined) {
        qc.removeQueries({ queryKey: favoritesKeys.ids, exact: true })
      } else {
        qc.setQueryData(favoritesKeys.ids, context.previous)
      }
    },
    onSettled: () => {
      // `['favorites']` prefix covers both the id set and the paginated favorites list.
      void qc.invalidateQueries({ queryKey: ['favorites'] })
      void qc.invalidateQueries({ queryKey: ['home-showcase', 'favorites'] })
    },
  })
}
